import { z } from "zod";
import { FORM_ERROR_MESSAGES } from "@shared/constants";

/**
 * architecture §5.6 -- `z.string().min(1).max(500)`, REQUIRED. Mirrored byte-for-byte on the web side,
 * the same discipline this feature uses everywhere a server-enforced string cap exists: a named,
 * exported constant shared between the Zod schema below and the DOM's own `maxLength`, never a bare
 * literal repeated in both places.
 *
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3: extracted out of
 * `DenyRedemptionRequestContainer.tsx` into this small leaf module so BOTH that dialog and
 * `PersonSparksDrawerBody`'s own inline reason box import the SAME schema/constant -- neither copies it.
 * Mirrors this domain's own established shape for a small standalone helper living beside its consumers
 * (`components/RecognitionListContainer/hasMore.ts`/`atFeedCap.ts`/`recognitionPersonName.ts`), not a new
 * abstraction pattern.
 */
export const DENY_REASON_MAX_LENGTH = 500;

// Exported so the raw schema can be tested directly against a 501-character string -- a consuming
// Textarea's own `maxLength={DENY_REASON_MAX_LENGTH}` attribute makes typing a 501st character
// unreachable through simulated keystrokes (the same reasoning applies to any DOM-capped field: once the
// attribute makes a longer string untypeable via simulated keystrokes, the boundary itself has to be
// proven directly against the schema instead), so the `.max()` rule itself needs a direct,
// DOM-independent test -- DenyRedemptionRequestContainer.test.tsx asserts the 500/501 boundary directly
// against it; PersonSparksDrawerBody.test.tsx asserts this same instance rejects empty and over-max.
export const validationSchema = z.object({
  reason: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.REQUIRED)
    .max(DENY_REASON_MAX_LENGTH, `Reason ${FORM_ERROR_MESSAGES.LONG}`),
});

export type DenyReasonFormShape = z.infer<typeof validationSchema>;
